// API Service Layer for Backend Communication

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Generic fetch wrapper with error handling
async function fetchAPI<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const config: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
}

// Health check
export async function checkHealth(): Promise<{ status: string; timestamp: string; uptime: number }> {
  return fetchAPI('/health');
}

// Auth API
export async function login(username: string, password: string, role: 'bank' | 'company', bankId?: string) {
  return fetchAPI<{
    user: {
      id: string;
      username: string;
      role: string;
      bankId?: string;
    };
    bank?: {
      id: string;
      name: string;
      code: string;
    };
  }>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password, role, bankId }),
  });
}

export async function register(username: string, password: string, role: 'bank' | 'company') {
  return fetchAPI<{
    user: {
      id: string;
      username: string;
      role: string;
    };
  }>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ username, password, role }),
  });
}

// Banks API
export async function getAllBanks() {
  return fetchAPI<{
    banks: Array<{
      id: string;
      name: string;
      code: string;
      created_at: string;
      updated_at: string;
    }>;
  }>('/api/banks');
}

export async function getBank(id: string) {
  return fetchAPI<{
    bank: {
      id: string;
      name: string;
      code: string;
      created_at: string;
      updated_at: string;
    };
  }>(`/api/banks/${id}`);
}

// Checklist API
export async function getChecklistRecords(
  bankId: string,
  checklistType: string
) {
  return fetchAPI<{
    records: any[];
  }>(`/api/checklist/${bankId}/${checklistType}`);
}

export async function getAllChecklistRecords(checklistType: string) {
  return fetchAPI<{
    records: any[];
  }>(`/api/checklist/all/${checklistType}`);
}

export async function saveChecklistRecord(
  record: any,
  parteA?: any,
  parteB?: any,
  parteC?: any,
  infoPrincipali?: any
) {
  return fetchAPI<{
    record: any;
  }>('/api/checklist', {
    method: 'POST',
    body: JSON.stringify({ record, infoPrincipali, parteA, parteB, parteC }),
  });
}

export async function deleteChecklistRecord(id: number) {
  return fetchAPI<{
    success: boolean;
  }>(`/api/checklist/${id}`, {
    method: 'DELETE',
  });
}

// Export API base URL for reference
export { API_BASE_URL };
